import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { Leg } from './leg.model';
import { AutomatedFuelDataForm } from './automated-fuel-data-form.model';

export class LegFuelForm {
  fuelOut: FormControl = new FormControl(0, Validators.max(999999.9999));
  fuelOff: FormControl = new FormControl(0, Validators.max(999999.9999));
  fuelOn: FormControl = new FormControl(0, Validators.max(999999.9999));
  fuelIn: FormControl = new FormControl(0, Validators.max(999999.9999));
  quantityTypeId: FormControl = new FormControl(0, Validators.max(999999.999));
  fuelBurn: FormControl = new FormControl(0);
  automatedFuelData: FormGroup | null;

  // index signature
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  constructor(leg?: Leg) {
    if (leg) {
      this.fuelOut.setValue(leg.fuelOut);
      this.fuelOff.setValue(leg.fuelOff);
      this.fuelOn.setValue(leg.fuelOn);
      this.fuelIn.setValue(leg.fuelIn);
      this.fuelBurn.setValue(leg.fuelBurn ? +leg.fuelBurn.toFixed(2) : leg.fuelBurn);
      this.quantityTypeId.setValue(leg.quantityTypeId);

      if (leg.automatedData && leg.automatedData.fuel) {
        this.automatedFuelData = new FormGroup(new AutomatedFuelDataForm(leg.automatedData.fuel));
      }
    }
  }
}

// validators
export function validateOooiValues(): ValidatorFn {
  return (formGroup: FormGroup): ValidationErrors | null => {
    const fuelOutControl = formGroup.get('fuelOut');
    const fuelOffControl = formGroup.get('fuelOff');
    const fuelOnControl = formGroup.get('fuelOn');
    const fuelInControl = formGroup.get('fuelIn');
    const fuelBurnControl = formGroup.get('fuelBurn');

    let fuelOut: number;
    let fuelOff: number;
    let fuelOn: number;
    let fuelIn: number;
    let fuelBurn: number;

    if (fuelOutControl) {
      fuelOutControl.setErrors(null);
      fuelOutControl.setErrors(validateMinMax(fuelOutControl, 0, 999999));
      fuelOut = fuelOutControl.value;
    }

    if (fuelOffControl) {
      fuelOffControl.setErrors(null);
      fuelOffControl.setErrors(validateMinMax(fuelOffControl, 0, 999999));
      fuelOff = fuelOffControl.value;
    }

    if (fuelOnControl) {
      fuelOnControl.setErrors(null);
      fuelOnControl.setErrors(validateMinMax(fuelOnControl, 0, 999999));
      fuelOn = fuelOnControl.value;
    }

    if (fuelInControl) {
      fuelInControl.setErrors(null);
      fuelInControl.setErrors(validateMinMax(fuelInControl, 0, 999999));
      fuelIn = fuelInControl.value;
    }

    if (fuelBurnControl) {
      fuelBurnControl.setErrors(null);
      fuelBurnControl.setErrors(validateMinMax(fuelBurnControl, 0, 999999));
      fuelBurn = fuelBurnControl.value;
    }

    return checkValues();

    function checkValues(): ValidationErrors | null {
      // check if any values entered
      if (fuelOut || fuelOff || fuelOn || fuelIn || fuelBurn) {
        const showErrors = (control: AbstractControl): ValidationErrors => {
          control.setErrors({ invalidFuelAmounts: true });
          control.markAsTouched();
          return { invalidFuelSequence: true };
        };

        // if 2 or more values are entered and they create a negative number, show error
        if (fuelBurnControl && fuelBurn < 0) {
          fuelBurnControl.setErrors({ invalidFuelAmount: true });
          fuelBurnControl.markAsTouched();
        }

        // if Off > Out
        if (fuelOffControl && fuelOff) {
          // check explicitly for field values in case the were deleted (Null)
          if (fuelOut && fuelOff > fuelOut) {
            return showErrors(fuelOffControl);
          }
        }

        // if On > Out/Off
        if (fuelOnControl && fuelOn && (fuelOut || fuelOff)) {
          // check explicitly for field values in case the were deleted (Null)
          if (fuelOff && fuelOn > fuelOff) {
            return showErrors(fuelOnControl);
          }
          if (fuelOut && fuelOn > fuelOut) {
            return showErrors(fuelOnControl);
          }
        }

        // if In > Out/Off/On
        if (fuelInControl && fuelIn && (fuelOut || fuelOff || fuelOn)) {
          // check explicitly for field values in case the were deleted (Null)
          if (fuelOn && fuelIn > fuelOn) {
            return showErrors(fuelInControl);
          }
          if (fuelOff && fuelIn > fuelOff) {
            return showErrors(fuelInControl);
          }
          if (fuelOut && fuelIn > fuelOut) {
            return showErrors(fuelInControl);
          }
        }
      }

      return null;
    }
  };
}

function validateMinMax(control: AbstractControl, min: number, max: number) {
  const minError = Validators.min(min)(control);
  if (minError) {
    return minError;
  }
  const maxError = Validators.max(max)(control);
  if (maxError) {
    return maxError;
  }
  return null;
}
